// Tester detail — shows person OR business field set, linked accounts table,
// edit shortcut, suppress/restore.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { lookups, testers } from '@/lib/api';
import { formatTransaction } from '@/lib/format';
import { useMe } from '@/hooks/useMe';
import { HardDeleteButton } from '@/components/HardDeleteButton';
import type { HardDeleteMode } from '@frt/shared-types';

export function TesterDetailPage() {
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = Number(idStr);
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';
  const canSuppress = me?.role === 'admin';

  const { data: tester, isLoading, error } = useQuery({
    queryKey: ['tester', id],
    queryFn: () => testers.get(id),
    enabled: Number.isFinite(id),
  });
  const { data: paymentStatuses } = useQuery({
    queryKey: ['lookups', 'payment-status-options'],
    queryFn: () => lookups.paymentStatusOptions.list(),
  });
  const paymentStatusName = (id: number | null): string => {
    if (id == null) return '—';
    const hit = paymentStatuses?.data.find((s) => s.id === id);
    return hit?.name ?? '—';
  };

  // Per ops feedback (mig 0010): testers don't have a separate "suppressed"
  // concept; Inactive is the mechanism that hides them from new-account
  // pickers. Suppress button flips tester_status to Inactive and back.
  const suppress = useMutation({
    mutationFn: () =>
      tester?.tester_status_name === 'Inactive' ? testers.restore(id) : testers.suppress(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tester', id] }),
  });
  const navigate = useNavigate();
  const deleteTester = useMutation({
    mutationFn: (body: { mode: HardDeleteMode; reason: string | null }) =>
      testers.remove(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['testers'] });
      void navigate({ to: '/testers' });
    },
  });

  if (!Number.isFinite(id)) return <div className="text-sm text-red-600">Invalid id</div>;
  if (isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="text-sm text-red-600">{(error as Error).message}</div>;
  if (!tester) return <div className="text-sm text-slate-500">Not found</div>;

  const display =
    tester.kind === 'person'
      ? tester.short_name ??
        `${tester.first_name ?? ''} ${tester.last_name ?? ''}`.trim()
      : tester.short_business_name ?? tester.business_name ?? '(unnamed business)';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">
          {display}{' '}
          <span className="text-sm text-slate-500 font-normal">
            ({tester.kind} · {tester.tester_status_name ?? '—'})
          </span>
        </h1>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Link to="/testers/$id/edit" params={{ id: String(id) }}>
              <Button variant="secondary">Edit</Button>
            </Link>
          )}
          {canSuppress && (
            <Button
              variant={tester.tester_status_name === 'Inactive' ? 'secondary' : 'destructive'}
              onClick={() => {
                if (
                  tester.tester_status_name === 'Inactive' ||
                  confirm('Suppress this tester? Snapshots remain valid.')
                ) {
                  suppress.mutate();
                }
              }}
            >
              {tester.tester_status_name === 'Inactive' ? 'Restore' : 'Suppress'}
            </Button>
          )}
          {me?.role === 'admin' && (
            <HardDeleteButton
              entityLabel="tester"
              onConfirm={(body) => deleteTester.mutate(body)}
            />
          )}
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Tester details</CardTitle>
        </CardHeader>
        <CardBody className="text-sm">
          <dl className="grid grid-cols-2 gap-y-1 gap-x-4">
            {tester.kind === 'person' ? (
              <>
                <dt className="text-slate-500">First name</dt>
                <dd>{tester.first_name ?? '—'}</dd>
                <dt className="text-slate-500">Last name</dt>
                <dd>{tester.last_name ?? '—'}</dd>
                <dt className="text-slate-500">Short name</dt>
                <dd className="font-mono">{tester.short_name ?? '—'}</dd>
              </>
            ) : (
              <>
                <dt className="text-slate-500">Business name</dt>
                <dd>{tester.business_name ?? '—'}</dd>
                <dt className="text-slate-500">Short business name</dt>
                <dd className="font-mono">{tester.short_business_name ?? '—'}</dd>
                <dt className="text-slate-500">Opened by</dt>
                <dd>
                  {tester.opened_by_first_name} {tester.opened_by_last_name}{' '}
                  <span className="font-mono">({tester.opened_by_initials})</span>
                </dd>
              </>
            )}
            <dt className="text-slate-500">Email</dt>
            <dd>{tester.email ?? '—'}</dd>
            <dt className="text-slate-500">Mobile</dt>
            <dd>{tester.mobile_phone ?? '—'}</dd>
          </dl>
          {tester.notes && (
            <div className="mt-3">
              <div className="text-slate-500 text-xs">Notes</div>
              <div className="whitespace-pre-wrap">{tester.notes}</div>
            </div>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Linked accounts ({tester.account_count})</CardTitle>
        </CardHeader>
        <CardBody>
          {tester.accounts.length === 0 ? (
            <div className="text-sm text-slate-500">No accounts.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-3 whitespace-nowrap">Display name</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Tiller</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Status</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Tester Relationship</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Contract Signed</th>
                    <th className="py-1 pr-3 whitespace-nowrap text-right">Contract Term</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Contract Date</th>
                    <th className="py-1 pr-3 whitespace-nowrap text-right">FRT Tester Fee</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Payment Status</th>
                    <th className="py-1 pr-3 whitespace-nowrap">Payment Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tester.accounts.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100 align-top">
                      <td className="py-1 pr-3">
                        <Link
                          to="/accounts/$id"
                          params={{ id: String(a.id) }}
                          className="text-brand-700 hover:underline font-mono"
                        >
                          {a.display_name}
                        </Link>
                      </td>
                      <td className="py-1 pr-3">{a.tiller_status}</td>
                      <td className="py-1 pr-3">{a.account_status}</td>
                      <td className="py-1 pr-3">
                        {a.tester_relationship === 'third_party'
                          ? 'Third Party'
                          : a.tester_relationship === 'internal'
                            ? 'Internal'
                            : '—'}
                      </td>
                      <td className="py-1 pr-3">
                        {a.contract_signed === true
                          ? 'Yes'
                          : a.contract_signed === false
                            ? 'No'
                            : '—'}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">
                        {a.contract_term_months ?? '—'}
                      </td>
                      <td className="py-1 pr-3 whitespace-nowrap">
                        {a.contract_signed_date ?? '—'}
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">
                        {formatTransaction(a.frt_tester_fee)}
                      </td>
                      <td className="py-1 pr-3">{paymentStatusName(a.payment_status_id)}</td>
                      <td className="py-1 pr-3 whitespace-nowrap">{a.payment_date ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
